import React, { useCallback, useMemo, useReducer, useState } from 'react';
import { node } from 'prop-types';
import useDebounceCallback from '@hooks/useDelayTask';
import StockBuySellReducer, { formAction, INITIAL_STATE } from './StockBuySellReducer';
import { FORM_OPTIONS, formKey } from './constants';

const INITIAL_CONTEXT_VALUE = {
  formState: INITIAL_STATE,
  formKey,
  formAction,
  dispatch: () => {},
  toggleOrderSide: () => {},
  setPrice: () => {},
  setLotCount: () => {},
  resetStockForm: () => {},
  resetAllForm: () => {},
};

const StockBuySellContext = React.createContext(INITIAL_CONTEXT_VALUE);

const StockBuySellProvider = ({ children }) => {
  const [activeCode, setActiveCode] = useState('');
  const [formState, dispatch] = useReducer(StockBuySellReducer, INITIAL_STATE);
  const delayOrderSide = useDebounceCallback(300);
  const delayAccountType = useDebounceCallback(300);
  // Dispatch action shortcuts

  const toggleAccountType = useCallback(
    (ticker, isDelay = false, accountType = 'REGULAR') => {
      if (!ticker) return;
      // REGULAR || DAY_TRADING || MARGIN_TRADING
      let objAccountType = { ...FORM_OPTIONS.ACCOUNT_TYPE[0] };
      if (accountType === 'DAY_TRADING') objAccountType = { ...FORM_OPTIONS.ACCOUNT_TYPE[1] };
      if (accountType === 'MARGIN_TRADING') objAccountType = { ...FORM_OPTIONS.ACCOUNT_TYPE[2] };
      if (isDelay) {
        // REGULAR || DAY_TRADING || MARGIN_TRADING
        delayAccountType(() =>
          dispatch({
            type: formAction.CHANGE_ACCOUNT_TYPE,
            code: ticker.code,
            payload: objAccountType,
          })
        );
        return;
      }
      dispatch({
        type: formAction.CHANGE_ACCOUNT_TYPE,
        code: ticker.code,
        payload: objAccountType,
      });
    },
    [dispatch, formAction, formState, formKey]
  );

  const toggleOrderSide = useCallback(
    (ticker, side = 'BUY', isRedirect = false, accountType = 'REGULAR') => {
      if (!ticker) return;
      // BUY || SELL
      if ((side && isRedirect, accountType)) {
        toggleAccountType(ticker, true, accountType);
        delayOrderSide(() =>
          dispatch({ type: formAction.CHANGE_ORDER_SIDE, code: ticker.code, payload: side })
        );
        return;
      }
      if (side && !isRedirect) {
        return dispatch({ type: formAction.CHANGE_ORDER_SIDE, code: ticker.code, payload: side });
      }
      const current = formState[ticker]?.[formKey.INPUT_ORDER_SIDE];
      return dispatch({
        type: formAction.CHANGE_ORDER_SIDE,
        code: ticker.code,
        payload: !current,
      });
    },
    [dispatch, formAction, formState, formKey]
  );

  const setPrice = useCallback(
    (code, price) => dispatch({ type: formAction.CHANGE_PRICE, code, payload: price }),
    [dispatch, formAction]
  );

  const setLotCount = useCallback(
    (code, lot) => dispatch({ type: formAction.CHANGE_LOT_COUNT, code, payload: lot }),
    [dispatch, formAction]
  );

  const resetStockForm = useCallback(
    (code) => dispatch({ type: formAction.RESET_STOCK_FORM, code }),
    [dispatch, formAction]
  );

  const resetAllForm = useCallback(
    () => dispatch({ type: formAction.RESET_ALL_FORM }),
    [dispatch, formAction]
  );

  const contextValue = useMemo(
    () => ({
      activeCode,
      setActiveCode,
      formState,
      formKey,
      formAction,
      dispatch,
      toggleOrderSide,
      setPrice,
      setLotCount,
      resetStockForm,
      resetAllForm,
    }),
    [
      activeCode,
      setActiveCode,
      formState,
      formKey,
      formAction,
      dispatch,
      toggleOrderSide,
      setPrice,
      setLotCount,
      resetStockForm,
      resetAllForm,
    ]
  );
  return (
    <StockBuySellContext.Provider value={contextValue}>{children}</StockBuySellContext.Provider>
  );
};

StockBuySellProvider.propTypes = {
  children: node,
};
StockBuySellProvider.defaultProps = {
  children: null,
};

export { StockBuySellContext, StockBuySellProvider };
